import { useState, useCallback } from "react";
import { api, ApiError } from "@/lib/api-client";

export interface UploadFile {
  file: File;
  id: string;
}

export interface UploadResult {
  batchId: string;
  name: string;
  documents: { id: string; filename: string }[];
  status: string;
}

export interface UseUploadReturn {
  files: UploadFile[];
  addFiles: (fileList: FileList | File[]) => void;
  removeFile: (id: string) => void;
  clearFiles: () => void;
  upload: (name: string, jobTypeId: string, declaredFormId?: string) => Promise<UploadResult>;
  isUploading: boolean;
  progress: number; // 0-100
  error: string | null;
}

let fileIdCounter = 0;

export function useUpload(): UseUploadReturn {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const addFiles = useCallback((fileList: FileList | File[]) => {
    const newFiles = Array.from(fileList).map((file) => ({
      file,
      id: `upload-${++fileIdCounter}`,
    }));
    setFiles((prev) => [...prev, ...newFiles]);
    setError(null);
  }, []);

  const removeFile = useCallback((id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const clearFiles = useCallback(() => {
    setFiles([]);
    setError(null);
    setProgress(0);
  }, []);

  const upload = useCallback(
    async (
      name: string,
      jobTypeId: string,
      declaredFormId?: string,
    ): Promise<UploadResult> => {
      if (files.length === 0) {
        throw new Error("No files selected");
      }

      setIsUploading(true);
      setProgress(0);
      setError(null);

      try {
        const formData = new FormData();
        formData.append("name", name);
        formData.append("jobTypeId", jobTypeId);
        // F-043 — source_declared batches carry a declared form for every doc.
        if (declaredFormId) formData.append("declaredFormId", declaredFormId);

        for (const { file } of files) {
          formData.append("files", file);
        }

        // Simulate progress since we don't have XHR progress events with fetch
        const progressInterval = setInterval(() => {
          setProgress((p) => Math.min(p + 5, 90));
        }, 200);

        const result = await api.upload<UploadResult>(
          "/batches/upload",
          formData,
        );

        clearInterval(progressInterval);
        setProgress(100);

        return result;
      } catch (err) {
        let message = "Upload failed";
        if (err instanceof ApiError) {
          const body = err.body as { error?: string } | null;
          if (err.status === 413) {
            message = body?.error ?? "File too large — maximum size is 50 MB per file.";
          } else if (err.status === 400) {
            message = body?.error ?? "File type not supported. Allowed: PDF, PNG, JPEG, TIFF, BMP, WebP.";
          } else {
            message = body?.error ?? `Upload failed (${err.status})`;
          }
        } else if (err instanceof Error) {
          message = err.message;
        }
        setError(message);
        throw err;
      } finally {
        setIsUploading(false);
      }
    },
    [files],
  );

  return {
    files,
    addFiles,
    removeFile,
    clearFiles,
    upload,
    isUploading,
    progress,
    error,
  };
}
